import React from 'react';
import styled from 'styled-components';
import { PurchaseSummaryResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import Tooltip from 'client/widget-components/components/Tooltip';
import { COLORS, sp } from '../styles';
import { DATA_GRABS } from '../design';
import ThemedText from './ThemedText';
import { formatCurrency } from '../utils';
import { useEcommStr } from '../useEcommStr';
import Divider from './Divider';
import { InfoBorder } from './icons';

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${sp(2)};
`;

// The role is applied to the row rather than each cell, so the label, the
// amount and the strikethrough original all follow it by inheritance.
const Line = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
})`
    display: flex;
    justify-content: space-between;
`;

const LineLabel = styled.span`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4em;
`;

const OriginalValue = styled.span`
    display: inline-block;
    text-decoration: line-through;
    margin-inline-end: ${sp(0.5)};
`;

const TaxesIncludedBox = styled.div.attrs({
    'data-grab': DATA_GRABS.dividers,
})`
    background-color: ${COLORS.bgAlt};
    border: 1px solid ${COLORS.border};
    border-radius: 5px;
    padding: ${sp(1)};
`;

const TooltipIcon = styled.span`
    cursor: help;
    display: inline-flex;
    align-items: center;
    color: ${COLORS.icon};

    svg {
        inline-size: 14px;
        block-size: 14px;
    }
`;

const TooltipSlot = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    line-height: 1;

    [data-auto='tooltip-content'] {
        position: absolute;
        inset-block-start: 100%;
        inset-inline-start: 50%;
        transform: translateX(-50%);
        margin: 0;
    }

    [data-auto='tooltip-content-box'] {
        max-width: 280px;
        width: max-content;
    }
`;

interface PurchaseSummaryProps {
    summary: PurchaseSummaryResponseDto;
    currency: string;
    hasTaxesIncludedInPrices: boolean;
    hasEstimatedTaxes?: boolean;
}

const PurchaseSummary: React.FC<PurchaseSummaryProps> = ({
    summary,
    currency,
    hasTaxesIncludedInPrices,
    hasEstimatedTaxes = false,
}) => {
    const str = useEcommStr();
    const fmt = (amount: number) => formatCurrency(amount, currency);

    const hasMultipleTaxes = !!summary.taxes && summary.taxes.length > 1;

    const displayFees =
        summary.fees && summary.fees.length > 0 ? summary.fees : null;

    const displayTaxes = (() => {
        if (!summary.taxes) return null;
        if (hasEstimatedTaxes) {
            return [
                {
                    name: str('nativeEcommRuntime.purchaseSummary.taxes.estimated'),
                    total_amount: summary.taxes.reduce(
                        (acc, t) => acc + t.total_amount,
                        0
                    ),
                    percentage: null as number | null,
                },
            ];
        }
        return summary.taxes.map((t) => ({
            name: t.name,
            total_amount: t.total_amount,
            percentage: t.percentage as number | null,
        }));
    })();

    const totalTaxIncludedLabel = (() => {
        if (!summary.total_taxes || !hasTaxesIncludedInPrices) return undefined;
        const taxAmount = fmt(summary.total_taxes);
        if (hasEstimatedTaxes) {
            return str('nativeEcommRuntime.purchaseSummary.taxes.includingEstimated', {
                taxAmount,
            });
        }
        if (summary.taxes?.length === 1) {
            const t = summary.taxes[0];
            const tax = `${taxAmount} ${t.name} (${t.percentage}%)`;
            return str('nativeEcommRuntime.purchaseSummary.taxes.includingSingle', { tax });
        }
        return str('nativeEcommRuntime.purchaseSummary.taxes.includingMultiple', { taxAmount });
    })();

    return (
        <Wrapper data-auto='cart-summary-fees'>
            {summary.subtotal_amount != null && (
                <Line>
                    <LineLabel>
                        {str('nativeEcommRuntime.purchaseSummary.subtotal.label')}
                    </LineLabel>
                    <span>
                        {summary.original_subtotal_amount != null && (
                            <OriginalValue>
                                {fmt(summary.original_subtotal_amount)}
                            </OriginalValue>
                        )}
                        {fmt(summary.subtotal_amount)}
                    </span>
                </Line>
            )}

            {displayFees?.map((fee) => (
                <Line key={fee.id}>
                    <LineLabel>
                        <span>{fee.name}</span>
                        {fee.description && (
                            <TooltipSlot>
                                <Tooltip text={fee.description}>
                                    <TooltipIcon aria-hidden='true'>
                                        <InfoBorder />
                                    </TooltipIcon>
                                </Tooltip>
                            </TooltipSlot>
                        )}
                    </LineLabel>
                    <span>{fmt(fee.amount)}</span>
                </Line>
            ))}

            {summary.discount_amount != null && (
                <Line>
                    <LineLabel>
                        {str('nativeEcommRuntime.purchaseSummary.discount.label')}
                    </LineLabel>
                    <span data-auto='cart-discounts'>
                        − {fmt(summary.discount_amount)}
                    </span>
                </Line>
            )}

            {summary.shipping_amount != null && (
                <Line>
                    <LineLabel>
                        {str('nativeEcommRuntime.purchaseSummary.shipping.label')}
                    </LineLabel>
                    <span>{fmt(summary.shipping_amount)}</span>
                </Line>
            )}

            {!hasTaxesIncludedInPrices && (
                <>
                    {summary.net_total_amount != null && (
                        <Line>
                            <LineLabel>
                                {str('nativeEcommRuntime.purchaseSummary.netTotal.label')}
                            </LineLabel>
                            <span>{fmt(summary.net_total_amount)}</span>
                        </Line>
                    )}

                    {summary.net_total_amount != null && <Divider />}

                    {displayTaxes?.map((tax) => (
                        <Line key={`${tax.name}_${tax.percentage ?? ''}`}>
                            <LineLabel>
                                <span>
                                    {tax.name}
                                    {tax.percentage ? ` (${tax.percentage}%)` : ''}
                                </span>
                                {hasEstimatedTaxes && (
                                    <TooltipSlot>
                                        <Tooltip
                                            text={str(
                                                'nativeEcommRuntime.purchaseSummary.taxes.notice.estimated'
                                            )}
                                        >
                                            <TooltipIcon aria-hidden='true'>
                                        <InfoBorder />
                                    </TooltipIcon>
                                        </Tooltip>
                                    </TooltipSlot>
                                )}
                            </LineLabel>
                            <span>{fmt(tax.total_amount)}</span>
                        </Line>
                    ))}

                    {!!summary.total_taxes && !hasEstimatedTaxes && (
                        <Line>
                            <LineLabel>
                                {str('nativeEcommRuntime.purchaseSummary.totalTaxes.label')}
                            </LineLabel>
                            <span>{fmt(summary.total_taxes)}</span>
                        </Line>
                    )}
                </>
            )}

            <Divider />

            {summary.total_amount != null && (
                <Line>
                    <LineLabel>
                        <span>
                            {str('nativeEcommRuntime.purchaseSummary.total.label')}{' '}
                            {totalTaxIncludedLabel}
                        </span>
                    </LineLabel>
                    <span data-auto='cart-total-amount'>
                        {fmt(summary.total_amount)}
                    </span>
                </Line>
            )}

            {hasTaxesIncludedInPrices &&
                hasMultipleTaxes &&
                !hasEstimatedTaxes && (
                    <TaxesIncludedBox>
                        {displayTaxes?.map((tax) => (
                            <Line key={`${tax.name}_${tax.percentage ?? ''}`}>
                                <LineLabel>
                                    <span>
                                        {tax.name}
                                        {tax.percentage ? ` (${tax.percentage}%)` : ''}
                                    </span>
                                </LineLabel>
                                <span>{fmt(tax.total_amount)}</span>
                            </Line>
                        ))}
                    </TaxesIncludedBox>
                )}
        </Wrapper>
    );
};

export default PurchaseSummary;
